import { useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Typography,
} from '@mui/material';
import { Logout as LogoutIcon, Person as PersonIcon } from '@mui/icons-material';
import { PropTypes } from 'prop-types';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import useAuth from '../../Context/Hooks/useCustomAuth';

const getInitials = (name) => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const AccountPopover = ({ name, department }) => {
  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();
  const { logout } = useAuth();

  const initials = getInitials(name);
  const avatarBg = theme.palette.brand?.avatarBg ?? '#5B82A8';
  const nameColor = theme.palette.brand?.nameColor ?? '#343434';

  const handleLogout = async () => {
    try {
      setOpen(false);
      await logout();
      navigate('/');
    } catch (err) {
      console.error(err);
      toast.error('Unable to logout.');
    }
  };

  return (
    <>
      {/* Trigger: avatar + name + role */}
      <Box
        component={ButtonBase}
        onClick={() => setOpen(true)}
        ref={anchorRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          borderRadius: 1,
          px: 0.75,
          py: 0.5,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Avatar
          sx={{
            width: 30,
            height: 30,
            bgcolor: avatarBg,
            fontSize: '0.7rem',
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {initials}
        </Avatar>
        <Box sx={{ textAlign: 'left' }}>
          <Typography
            sx={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              lineHeight: 1.3,
              color: nameColor,
              whiteSpace: 'nowrap',
            }}
          >
            {name || '—'}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.6875rem',
              color: 'text.secondary',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
            }}
          >
            {department || ''}
          </Typography>
        </Box>
      </Box>

      {/* Dropdown menu */}
      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        keepMounted
        onClose={() => setOpen(false)}
        open={open}
        PaperProps={{ sx: { width: 220, mt: 0.5 } }}
      >
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography
variant="subtitle2"
fontWeight={600}
          >
            {name}
          </Typography>
          <Typography
variant="caption"
color="text.secondary"
          >
            {department}
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ py: 0.5 }}>
          <MenuItem
component={RouterLink}
to="/Gateway/Profile"
onClick={() => setOpen(false)}
          >
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2">
                  {t('profile:common.profile')}
                </Typography>
              }
            />
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2">
                  {t('common:common.logout')}
                </Typography>
              }
            />
          </MenuItem>
          {process.env.REACT_APP_CUSTOM_AUTH === 'LDAP' && (
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    {t('common:common.logout')}
                  </Typography>
                }
              />
            </MenuItem>
          )}
        </Box>
      </Popover>
    </>
  );
};

AccountPopover.propTypes = {
  name: PropTypes.string,
  department: PropTypes.string,
};

export default AccountPopover;
